// VisorLectura.js — Contenido de lectura (texto) dentro del reproductor del modo Mentor.
//
// El texto se guarda plano en la biblioteca (mentor_media.descripcion, tipo
// 'lectura') y se edita desde el admin. Aquí se arma: una línea en blanco separa
// párrafos, un salto simple es un salto de línea y los enlaces se vuelven clicables
// (sueltos, o con título a la manera de Markdown: [Título](https://…)).
// No se interpreta HTML: lo que venga se muestra como texto.
import React, { useState } from 'react';
import styled from 'styled-components';
import VisorPdf from './VisorPdf';

const Marco = styled.div`
  position: absolute;
  inset: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  background: var(--m-bg-card, #111827);
  color: var(--m-text-primary, #e5e7eb);
`;

const Hoja = styled.article`
  max-width: 72ch;
  margin: 0 auto;
  padding: clamp(1.25rem, 4vw, 2.5rem) clamp(1rem, 4vw, 2rem) 3rem;
  font-size: 1rem;
  line-height: 1.7;

  h2 {
    font-size: clamp(1.15rem, 2.4vw, 1.45rem);
    line-height: 1.3;
    margin: 0 0 1.25rem;
    color: var(--m-text-primary, #f9fafb);
  }
  p { margin: 0 0 1rem; overflow-wrap: anywhere; }
  a { color: #22d3ee; text-decoration: underline; text-underline-offset: 2px; }
  a:focus-visible { outline: 2px solid #22d3ee; outline-offset: 2px; border-radius: 2px; }
  a.pdf::before { content: 'PDF'; display: inline-block; margin-right: .4rem; padding: 0 .3rem; border-radius: 3px;
    font-size: .68rem; font-weight: 700; line-height: 1.45; vertical-align: .12em; color: #111827; background: #22d3ee; text-decoration: none; }
`;
// PDF abierto desde la lectura: el visor ocupa el recuadro, con «Volver a la lectura» arriba.
const MarcoPdf = styled.div`
  position: absolute; inset: 0; display: flex; flex-direction: column;
  > button { flex: none; align-self: flex-start; margin: .45rem .6rem; padding: .35rem .7rem; border: 0; border-radius: 6px;
    background: rgba(255,255,255,.12); color: #fff; font: inherit; font-size: .82rem; cursor: pointer; }
  > button:hover, > button:focus-visible { background: rgba(255,255,255,.22); outline: none; }
  > div { position: relative; flex: 1; min-height: 0; }
`;

// [Título](url) o una URL suelta. Grupos: 1 título, 2 url con título, 3 url suelta.
const ENLACE_RE = /\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s<>"']+[^\s<>"'.,;:)\]])/g;

// PDF guardado en la plataforma (…/backend/uploads/…pdf, de cualquiera de nuestros dominios):
// se abre aquí mismo, en el visor, en vez de otra pestaña. Devuelve la ruta relativa o null.
const pdfPropio = (href) => {
  const m = String(href || '').match(/^(?:https?:\/\/[^/]*ateneo(?:mentoria\.com|\.co)|)(\/backend\/uploads\/[^?#]+\.pdf)(?:[?#].*)?$/i);
  return m ? m[1] : null;
};

// Una línea de texto con sus enlaces convertidos en <a>.
const conEnlaces = (linea, clave, abrirPdf) => {
  const out = [];
  let ultimo = 0; let m; let n = 0;
  ENLACE_RE.lastIndex = 0;
  while ((m = ENLACE_RE.exec(linea)) !== null) {
    if (m.index > ultimo) out.push(linea.slice(ultimo, m.index));
    const href = m[2] || m[3];
    const propio = abrirPdf && pdfPropio(href);
    const titulo = m[1] || href;
    out.push(propio
      ? <a key={`${clave}-${n++}`} href={propio} onClick={(e) => { e.preventDefault(); abrirPdf({ url: propio, titulo }); }} className="pdf">{titulo}</a>
      : <a key={`${clave}-${n++}`} href={href} target="_blank" rel="noopener noreferrer">{titulo}</a>);
    ultimo = m.index + m[0].length;
  }
  if (ultimo < linea.length) out.push(linea.slice(ultimo));
  return out;
};

const VisorLectura = ({ titulo, texto }) => {
  const [pdf, setPdf] = useState(null);   // { url, titulo } del PDF abierto desde un enlace
  if (pdf) {
    return (
      <MarcoPdf>
        <button type="button" onClick={() => setPdf(null)}>← Volver a la lectura</button>
        <div><VisorPdf key={pdf.url} url={pdf.url} titulo={pdf.titulo} descargar={false} /></div>
      </MarcoPdf>
    );
  }
  const parrafos = String(texto || '').replace(/\r\n?/g, '\n').split(/\n\s*\n/).map(p => p.trim()).filter(Boolean);
  return (
    <Marco tabIndex={0} aria-label={titulo || 'Lectura'}>
      <Hoja>
        {titulo && <h2>{titulo}</h2>}
        {parrafos.map((p, i) => (
          <p key={i}>
            {p.split('\n').map((linea, j, todas) => (
              <React.Fragment key={j}>
                {conEnlaces(linea, `${i}-${j}`, setPdf)}
                {j < todas.length - 1 && <br />}
              </React.Fragment>
            ))}
          </p>
        ))}
      </Hoja>
    </Marco>
  );
};

export default VisorLectura;
